/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {ToastrService} from 'ngx-toastr';

import template from './library-creative-context-actions.component.html';
import {wireModelCommsModelActions} from '../common/controller/model-comms-actions';
import {LibraryCreativeCopyModalService} from './library-creative-copy-modal.service';
import {LibraryCreativeLinkCreativeModalService} from './library-creative-link-creative-modal.service';
import {LibraryCreativeResource} from '../../components/api/resources/resource/library-creative.resource';
import {deferMarkForCheck} from '../common/util/defer-mark-for-check';
import {bufferTemplateFlag as bufferSharedTemplateFlag, BufferedFlag} from '../common/util/template-state';
import {StateService} from '@uirouter/core';

@Component({
  standalone: false,
  selector: 'adn-library-creative-context-actions',
  template
})
export class LibraryCreativeContextActionsComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  @Input() modelComms: any;
  @Input() isNew: boolean;
  @Input() readOnly: boolean;
  @Input() blockDelete: any;

  /**
   * Route isNew never flips after save; modelComms does. Bind templates to this
   * so a sync subscriber callback cannot mutate the checked [isNew] mid-CD (NG0100).
   */
  effectiveIsNew = false;

  private readonly isNewFlag = {display: false, last: undefined as boolean | undefined};
  private readonly readOnlyFlag = {display: false, last: undefined as boolean | undefined};
  private readonly blockDeleteFlag = {display: false, last: undefined as boolean | undefined};

  private subscribedModelComms: any;
  private destroyed = false;

  constructor(
    private readonly $state: StateService,
    private readonly toastr: ToastrService,
    @Inject(LibraryCreativeResource) private readonly LibraryCreative: any,
    private readonly copyModalService: LibraryCreativeCopyModalService,
    private readonly linkCreativeModalService: LibraryCreativeLinkCreativeModalService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  get displayIsNew(): boolean {
    return this.bufferTemplateFlag(this.isNewFlag, !!this.effectiveIsNew);
  }

  get displayReadOnly(): boolean {
    return this.bufferTemplateFlag(this.readOnlyFlag, !!this.readOnly);
  }

  get displayBlockDelete(): boolean {
    return this.bufferTemplateFlag(this.blockDeleteFlag, !!this.blockDelete);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.setEffectiveIsNew(!!this.isNew);
    }
    if (this.modelComms) {
      wireModelCommsModelActions(this.modelComms, this.LibraryCreative);
    }
    this.ensureModelCommsSubscription();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  createCreative(): void {
    if (this.readOnly || this.blockDelete || !this.model) {
      return;
    }
    this.linkCreativeModalService.open({
      model: this.model,
      modelComms: this.modelComms,
      onComplete: (result) => {
        if (result.destination && result.creativeId) {
          this.$state.go('app.adv.creatives.creative', {id: result.creativeId});
        } else if (result.creativeId) {
          this.toastr.success(
            'A creative linking to this library creative was successfully created.',
            'Creative Created'
          );
        }
      }
    });
  }

  copy(): void {
    if (this.readOnly || this.blockDelete || !this.model) {
      return;
    }
    this.copyModalService.open({
      model: this.model,
      onComplete: (result) => {
        if (result.destination && result.creativeId) {
          this.$state.go('app.adv.library-creatives.library-creative', {id: result.creativeId});
        } else if (result.creativeId) {
          this.toastr.success(
            'A copy of this library creative was successfully created.',
            'Library Creative Copied'
          );
        }
      }
    });
  }

  private setEffectiveIsNew(value: boolean): void {
    if (this.effectiveIsNew === value) {
      return;
    }
    this.effectiveIsNew = value;
  }

  private bufferTemplateFlag(
    state: BufferedFlag,
    next: boolean
  ): boolean {
    return bufferSharedTemplateFlag(state, next, {
      isAlive: () => !this.destroyed,
      onCommit: () => this.cdr.markForCheck()
    });
  }

  private ensureModelCommsSubscription(): void {
    if (!this.modelComms || this.subscribedModelComms === this.modelComms) {
      return;
    }

    this.subscribedModelComms = this.modelComms;
    this.modelComms.addSubscriber((data: any, type: any, isNew: boolean) => {
      if (this.destroyed) {
        return;
      }
      // addSubscriber / modelUpdated can run synchronously during CD (including
      // immediate replay). Defer so [isNew] does not flip true→false after it
      // was checked (NG0100).
      deferMarkForCheck(this.cdr, () => {
        if (this.destroyed) {
          return;
        }
        if (typeof isNew === 'boolean') {
          this.setEffectiveIsNew(isNew);
        }
        if (type || !this.model || !data || this.model === data) {
          return;
        }
        if (data.name !== undefined) {
          this.model.name = data.name;
        }
      });
    });
  }
}
